import { CheckCircle2, Download, Plus, Send } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { Button } from "../button";
import { formatPriceNOK } from "../../../utilities/conversions";
import Badge from "../badge";
import { CreateInvoiceModal } from "./invoicemodal.tsx";
import axios from "axios";
import { SendInvoiceModal } from "./sendinvoicemodal";
import { set } from "jodit/esm/core/helpers";

export default function Invoices({ baseUrl, client, currentUser, outstanding }: { baseUrl: string, client: any, currentUser: any, outstanding: number }) {

    const initialInvoices = [
        { id: "INV-1043", number: "", date: "2025-05-04", customer: "Nordic Elektro AS", amount: 14500, vat: 25, status: "Pending", pdf: "https://example.com/invoices/INV-1043.pdf", link: "https://example.com/pay/INV-1043" },
        { id: "INV-1042", number: "", date: "2025-05-02", customer: "Hjem & Hage DA", amount: 8200, vat: 25, status: "Paid", pdf: "https://example.com/invoices/INV-1042.pdf", link: "https://example.com/pay/INV-1042" },
        { id: "INV-1041", number: "", date: "2025-04-28", customer: "Trygg Regnskap", amount: 3600, vat: 25, status: "Overdue", pdf: "https://example.com/invoices/INV-1041.pdf", link: "https://example.com/pay/INV-1041" },
    ];

    const [loading, setLoading] = useState(false);

    const getInvoices = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/getAgentInvoices`, { params: { token: currentUser.token } });

            if (response.data.status === "OK") {
                setInvoices(response.data.data);
            }
        } catch (err) {
            console.error("Error fetching invoices:", err);
        } finally {
            setLoading(false);
        }
    }

    const [invoices, setInvoices] = useState(initialInvoices);

    useMemo(() => {
        if (!client || !currentUser || !baseUrl) return {};

        getInvoices();

    }, [baseUrl, client, currentUser]);

    useMemo(() => {
        const out = invoices.filter(i => i.status !== "Paid").reduce((a, b) => a + b.amount, 0);
        outstanding = out;
        return {};
    }, [invoices]);

    // New invoice form state
    const [form, setForm] = useState({
        customer: "",
        email: "",
        org: "",
        description: "",
        qty: 1,
        unitPrice: 1000,
        vat: 25,
        dueDays: 14,
        fastpris: false,
    });

    type Lang = "no" | "en";
    const [show, setShow] = useState(false);

    const [lang, setLang] = useState<Lang>("no");
    const [open, setOpen] = useState(true);
    const [toast, setToast] = useState("");
    const toastTimerRef = useRef<number | undefined>(undefined);

    const showToast = (message: string) => {
        setToast(message);
        if (toastTimerRef.current !== undefined) window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = window.setTimeout(() => setToast(""), 3000);
    };



    const [showSendInvoice, setShowSendInvoice] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48">
                <span className="text-sm text-slate-500">Laster fakturaer...</span>
            </div>
        );
    }

    const handleInvoicePDF = (pdfUrl: string) => () => {
        window.open(pdfUrl, "_blank");
    }


    const handleInvoiceSend = (invoice: any) => () => {
        //open popup with email form, prefill with invoice data, on submit send email via API
        setSelectedInvoice(invoice);
        setShowSendInvoice(true);
    }


    return (
        <>
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 mb-3 mr-auto">
                <h3 className="font-bold text-purple-900">Faktura</h3>
                <Button onClick={() => setShow(true)} className="bg-gradient-to-r from-purple-600 to-violet-600"><Plus className="mr-2 h-4 w-4" />Ny faktura</Button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="rounded-lg border p-3">
                    <div className="text-slate-600">Fakturaer sendt</div>
                    <div className="mt-1 text-2xl font-bold">{invoices.length}</div>
                </div>
                <div className="rounded-lg border p-3">
                    <div className="text-slate-600">Utestående</div>
                    <div className="mt-1 text-2xl font-bold">{formatPriceNOK(outstanding)}</div>
                </div>
            </div>
            <div className="grid text-sm mt-4">
                <div className="text-sm font-medium mb-2">Siste fakturaer</div>
                <div className="overflow-hidden rounded-xl border">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="px-3 py-2 text-left">#</th>
                                <th className="px-3 py-2 text-left">Dato</th>
                                <th className="px-3 py-2 text-left">Kunde</th>
                                <th className="px-3 py-2 text-right">Beløp</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-right">Handlinger</th>
                            </tr>
                        </thead>
                        <tbody className="overflow-x-auto">
                            {invoices.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-3 py-4 text-center text-slate-500">Ingen fakturaer funnet.</td>
                                </tr>
                            )}
                            {invoices.map((inv) => (
                                <tr key={inv.id} className="border-t">
                                    <td className="px-3 py-2">{inv.number ? inv.number : inv.id.substring(0, 5) + "..."}</td>
                                    <td className="px-3 py-2">{inv.date}</td>
                                    <td className="px-3 py-2">{inv.customer}</td>
                                    <td className="px-3 py-2 text-right">{formatPriceNOK(inv.amount)}</td>
                                    <td className="px-3 py-2">
                                        {inv.status === "Paid" ? (
                                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle2 className="mr-1 h-3 w-3" />Betalt</Badge>
                                        ) : inv.status === "Overdue" ? (
                                            <Badge className="bg-rose-100 text-rose-700 border border-rose-200">Forfalt</Badge>
                                        ) : (
                                            <Badge variant="secondary">Venter</Badge>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <Button onClick={handleInvoicePDF(inv.pdf)}><Download className="mr-1 h-4 w-4" />PDF</Button>
                                            <Button onClick={handleInvoiceSend(inv)}><Send className="mr-1 h-4 w-4" />Send</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div >
            {/* <InvoiceModal
                show={show}
                onClose={() => setShow(false)}
                baseUrl={baseUrl}
                form={form}
                setForm={setForm}
            /> */}

            {show && (
                <CreateInvoiceModal lang={lang} onLangChange={setLang} onClose={() => setShow(false)} onToast={(message) => showToast(message)}
                    baseUrl={baseUrl}
                    client={client}
                    currentUser={currentUser}
                />
            )}

            {showSendInvoice && selectedInvoice && (
                <SendInvoiceModal
                    show={showSendInvoice}
                    onClose={() => setShowSendInvoice(false)}
                    selectedInvoice={selectedInvoice}
                    baseUrl={baseUrl}
                    currentUser={currentUser}
                    client={client}
                />
            )}
        </>
    )
}